import type { PopperProps } from "@mui/material";
import { ClickAwayListener, Fade } from "@mui/material";
import { useIntersectionObserver, useToggle } from "@react-hookz/web";
import type { ReactNode } from "react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSdkContext } from "scmp-sdk/components";

import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import type { Props as DashboardDisplayProps } from "./display";
import { useReadingHistorySetup } from "./display/reading-history-panel/hooks";
import { useIsFirstTimeVisitDashboardWidget } from "./hooks";
import CloseIcon from "./icon-close.svg";
import LogoIcon from "./icon-logo.svg";
import {
  Container,
  DisplayDrawer,
  DisplayPopper,
  DrawerHeader,
  MenuContainer,
  StyledDashboardDisplay,
  StyledIconButton,
} from "./styles";
import type { Props as DashboardTooltipsProps } from "./tooltips";
import { DashboardTooltips } from "./tooltips";

export type Props = Pick<DashboardDisplayProps, "messageRenders"> &
  Pick<DashboardTooltipsProps, "tooltipRenders"> & {
    children: ReactNode;
    className?: string;
  };

export const DashboardWidget: React.FunctionComponent<Props> = ({
  children,
  className,
  messageRenders,
  tooltipRenders,
}) => {
  const { hasAcceptedGDPR } = useSdkContext();

  const { config, handleOpenLoginDialog, user } = useSdkContext();

  useReadingHistorySetup();

  const anchorElement = useRef<HTMLDivElement>(null);

  const [isTooltipVisible, setIsTooltipVisible] = useToggle(true);
  const [isOpen, setIsOpen] = useState(false);
  const observer = useIntersectionObserver(anchorElement);
  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);
  const isTabletUp = useResponsive(theme.breakpoints.up("tablet"), false);
  const isDesktop = useResponsive(theme.breakpoints.up("desktop"), false);
  const renderDisplay = () => (
    <StyledDashboardDisplay
      beforeHeaderSlot={
        <DrawerHeader>
          <LogoIcon />
          <StyledIconButton aria-label="close" disableRipple onClick={() => setIsOpen(false)}>
            <CloseIcon />
          </StyledIconButton>
        </DrawerHeader>
      }
      messageRenders={messageRenders}
    />
  );

  const { setIsFirstTime } = useIsFirstTimeVisitDashboardWidget();
  useEffect(() => {
    if (!isOpen || !hasAcceptedGDPR) return;
    setIsFirstTime(false);
  }, [hasAcceptedGDPR, isOpen, setIsFirstTime]);

  useEffect(() => {
    setIsTooltipVisible(observer?.isIntersecting);
  }, [observer?.isIntersecting, setIsTooltipVisible]);

  const popperModifiers = useMemo<PopperProps["modifiers"]>(
    () => [
      {
        name: "offset",
        options: {
          // This is for handling tablet placement of the popper
          offset: [isDesktop ? 0 : 42, 10],
        },
      },
    ],
    [isDesktop],
  );

  const scrollHandler = () => setIsOpen(false);

  useEffect(() => {
    if (isOpen && isDesktop) {
      window.addEventListener("scroll", scrollHandler);
    } else {
      return () => {
        window.removeEventListener("scroll", scrollHandler);
      };
    }
    return () => {
      window.removeEventListener("scroll", scrollHandler);
    };
  }, [isDesktop, isOpen]);

  return (
    <>
      <DashboardTooltips
        isDashboardOpen={isOpen}
        isTooltipVisible={isTooltipVisible}
        tooltipRenders={tooltipRenders}
      >
        <Container
          className={className}
          onClick={() => {
            // setTimeout 0 is a hack for quick INP
            setTimeout(() => {
              if (isTabletUp) {
                setIsOpen(true);
              } else {
                if (user) {
                  window.location.href = config.accounts.host;
                } else {
                  handleOpenLoginDialog({
                    destination: globalThis.location.href,
                    ga4CustomParameter: {
                      trigger_point: "header",
                    },
                  });
                }
              }
            }, 0);
          }}
          ref={anchorElement}
        >
          {children}
        </Container>
      </DashboardTooltips>
      <DisplayDrawer
        anchor="bottom"
        hideBackdrop
        onClose={() => setIsOpen(false)}
        open={isMobile && isOpen}
        SlideProps={{
          easing: { enter: "ease" },
          timeout: { enter: 500, exit: 0 },
        }}
      >
        {renderDisplay()}
      </DisplayDrawer>
      <DisplayPopper
        anchorEl={anchorElement.current}
        modifiers={popperModifiers}
        open={!isMobile && isOpen}
        placement="bottom-end"
        transition
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={() => setIsOpen(false)}>
            <Fade {...TransitionProps} timeout={{ enter: 500, exit: 0 }}>
              <MenuContainer>{renderDisplay()}</MenuContainer>
            </Fade>
          </ClickAwayListener>
        )}
      </DisplayPopper>
    </>
  );
};
